import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { PDFDocument } from "pdf-lib";

/**
 * Generate and download a PDF document
 */
export const downloadPDF = async (
  document: React.ReactElement,
  fileName: string
) => {
  try {
    const blob = await pdf(document as React.ReactElement<any>).toBlob();
    saveAs(blob, fileName);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};

/**
 * Generate PDF as blob
 */
export const generatePDFBlob = async (
  document: React.ReactElement
): Promise<Blob> => {
  try {
    const blob = await pdf(document as React.ReactElement<any>).toBlob();
    return blob;
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};

/**
 * Generate PDF as URL
 */
export const generatePDFUrl = async (
  document: React.ReactElement
): Promise<string> => {
  try {
    const blob = await pdf(document as React.ReactElement<any>).toBlob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};

/**
 * Render a single react-pdf Document to a Uint8Array using a fresh isolated
 * pdf() instance. Each call creates its own instance and calls updateContainer(null)
 * after rendering to fully reset the shared reconciler before the next render.
 */
const renderToBytes = async (doc: React.ReactElement): Promise<Uint8Array> => {
  const instance = pdf();
  instance.updateContainer(doc as React.ReactElement<any>);
  const blob = await instance.toBlob();
  // Reset the container so the reconciler is fully torn down before next render
  instance.updateContainer(null as any);
  const arrayBuffer = await blob.arrayBuffer();
  return new Uint8Array(arrayBuffer);
};

/**
 * Generate two PDF documents, merge them with pdf-lib, and download.
 *
 * Each document is rendered using its own isolated pdf() instance with a
 * container reset between renders. This avoids the shared-reconciler corruption
 * that occurs when two react-pdf renders overlap or run back-to-back without
 * a proper teardown.
 */
export const downloadMergedPDF = async (
  mainDoc: React.ReactElement,
  appendDoc: React.ReactElement,
  fileName: string
): Promise<void> => {
  try {
    // Render main document, reset reconciler, then render append document
    const mainBytes = await renderToBytes(mainDoc);
    const appendBytes = await renderToBytes(appendDoc);

    const mainPdf = await PDFDocument.load(mainBytes);
    const appendPdf = await PDFDocument.load(appendBytes);

    const appendPageCount = appendPdf.getPageCount();
    if (appendPageCount === 0) {
      console.warn("appendDoc produced 0 pages — saving main doc only");
      saveAs(new Blob([mainBytes as Uint8Array<ArrayBuffer>], { type: "application/pdf" }), fileName);
      return;
    }

    const copiedPages = await mainPdf.copyPages(
      appendPdf,
      appendPdf.getPageIndices()
    );
    copiedPages.forEach((page) => mainPdf.addPage(page));

    const mergedBytes = await mainPdf.save();
    const mergedBuffer = mergedBytes.buffer.slice(
      mergedBytes.byteOffset,
      mergedBytes.byteOffset + mergedBytes.byteLength
    ) as ArrayBuffer;

    saveAs(new Blob([mergedBuffer], { type: "application/pdf" }), fileName);
  } catch (error) {
    console.error("Error merging PDFs:", error);
    throw error;
  }
};